import { useCallback, useEffect, useRef, useState } from 'react';
import { validateDocumentFile, validateDocumentMetadata } from '@/lib/documentPresentation';
import { runPoAttachmentFlow } from '@/lib/poAttachmentFlow';
import { poDraftTransport, poSaveError } from '@/lib/poDraftApi';

export default function usePoAttachmentWorkflow({ draftId, canView, canUpload, onSaved, onStatus, onFinancialVerified }) {
  const [queue, setQueueState] = useState([]);
  const queueRef = useRef([]);
  const [documents, setDocuments] = useState([]);
  const [listError, setListError] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('');
  const [paused, setPaused] = useState(null);
  const [reviewed, setReviewed] = useState(false);
  const alive = useRef(false);
  const running = useRef(false);
  const requestNumber = useRef(0);
  const controller = useRef(null);
  const savedId = useRef(draftId);
  const callbacks = useRef({ onSaved, onStatus, onFinancialVerified });
  callbacks.current = { onSaved, onStatus, onFinancialVerified };
  const updateQueue = useCallback(next => {
    queueRef.current = typeof next === 'function' ? next(queueRef.current) : next;
    if (alive.current) setQueueState(queueRef.current);
  }, []);
  const patchEntry = (key, values) => updateQueue(rows => rows.map(row => row.key === key ? { ...row, ...values } : row));

  const reload = useCallback(async (id = savedId.current) => {
    controller.current?.abort();
    const current = ++requestNumber.current;
    if (!canView || !id) { setDocuments([]); setListLoading(false); return []; }
    const abort = new AbortController(); controller.current = abort;
    setListLoading(true); setListError('');
    try {
      const rows = await poDraftTransport.list(id, abort.signal);
      if (alive.current && current === requestNumber.current) setDocuments(rows);
      return rows;
    } catch (cause) {
      if (alive.current && !abort.signal.aborted && current === requestNumber.current) {
        setDocuments([]); setListError(poSaveError(cause));
      }
      throw cause;
    } finally {
      if (alive.current && current === requestNumber.current) setListLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; controller.current?.abort(); requestNumber.current++; };
  }, []);
  useEffect(() => {
    savedId.current = draftId;
    setDocuments([]); setListError('');
    reload(draftId).catch(() => {});
  }, [draftId, reload]);

  const hasLocalFiles = queue.some(row => row.state !== 'saved');
  useEffect(() => {
    if (!hasLocalFiles && !paused && !phase) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    const link = event => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const target = new URL(anchor.href, window.location.href);
      if (target.href === window.location.href) return;
      if (running.current || !window.confirm('Selected files may not be saved yet. Leave this PO form?')) {
        event.preventDefault(); event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', link, true);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', link, true); };
  }, [hasLocalFiles, paused, phase]);

  const addFiles = files => {
    if (!canUpload || running.current || paused) return;
    try {
      const selected = Array.from(files || []);
      if (selected.length + queueRef.current.length > 10) throw new Error('Select at most 10 files per batch. Save this batch before adding more.');
      selected.forEach(validateDocumentFile);
      const keys = new Set(queueRef.current.map(row => `${row.file.name}:${row.file.size}:${row.file.lastModified}`));
      const additions = [];
      for (const file of selected) {
        const signature = `${file.name}:${file.size}:${file.lastModified}`;
        if (keys.has(signature)) continue;
        keys.add(signature);
        additions.push({ key: crypto.randomUUID(), file, title: file.name.replace(/\.[^.]+$/, '').slice(0, 200) || file.name, category: 'other', notes: '', state: 'queued', progress: 0 });
      }
      updateQueue(rows => [...rows, ...additions]); setError('');
    } catch (cause) { setError(cause.message); }
  };
  const editEntry = (key, patch) => {
    if (running.current || paused) return;
    updateQueue(rows => rows.map(row => row.key === key && ['queued','failed'].includes(row.state) ? { ...row, ...patch } : row));
  };
  const removeEntry = key => {
    if (running.current || paused) return;
    updateQueue(rows => rows.filter(row => row.key !== key || !['queued','failed'].includes(row.state)));
  };

  const run = async ({ intent, payload }) => {
    if (running.current || paused) return null;
    if (queueRef.current.some(row => row.state !== 'saved') && (!canView || !canUpload)) {
      setError('PO View and PO Edit are required to attach supporting files.'); return null;
    }
    try {
      for (const row of queueRef.current) {
        if (row.state === 'saved') continue;
        validateDocumentFile(row.file);
        validateDocumentMetadata({ document_type: row.category, title: row.title, description: row.notes });
      }
    } catch (cause) { setError(cause.message); return null; }
    running.current = true; setError(''); setReviewed(false);
    try {
      const result = await runPoAttachmentFlow({
        intent, draftId: savedId.current, payload, queue: queueRef.current.slice(), transport: poDraftTransport,
        isCurrent: () => alive.current,
        onPhase: next => { if (alive.current) setPhase(next); },
        onDraftSaved: po => {
          savedId.current = po.po_id;
          if (alive.current) callbacks.current.onSaved(po);
        },
        onFileState: (key, patch) => { if (alive.current) patchEntry(key, patch); },
        onFinancialVerified: data => { if (alive.current) callbacks.current.onFinancialVerified?.(data); },
      });
      if (!alive.current) return null;
      if (result.documents) setDocuments(result.documents);
      updateQueue([]); // Clear browser copies only after the full operation is confirmed.
      return result;
    } catch (cause) {
      if (!alive.current) return null;
      setError(poSaveError(cause) + (cause.poId ? ` Saved PO reference: #${cause.poId}.` : ''));
      if (cause.uncertain || cause.reviewRequired || cause.cause?.response?.status === 409) setPaused({ phase: cause.phase, poId: cause.poId, reviewRequired:cause.reviewRequired });
      if (cause.poId && canView) reload(cause.poId).catch(() => {});
      return null;
    } finally {
      running.current = false;
      if (alive.current) setPhase('');
    }
  };

  const reviewSavedState = async () => {
    if (running.current) return;
    if (!savedId.current) {
      if (window.confirm('Check both Draft and Pending PO lists first. Confirm that no order was created before allowing a new create request.')) {
        setPaused(null); setError('');
      }
      return;
    }
    running.current = true; setReviewed(false);
    try {
      const data = await poDraftTransport.summary(savedId.current);
      if (!alive.current) return;
      const header = data?.header;
      if (String(header?.po_id) !== String(savedId.current) || typeof header.status !== 'string') throw new Error('Unable to confirm the saved PO state.');
      callbacks.current.onStatus(header.status);
      await reload(savedId.current);
      if (!alive.current) return;
      if (header.status !== 'Draft') {
        setError(`PO #${header.po_id} is now ${header.status}. No further save or submission will be sent from this form.`);
        return;
      }
      if (paused?.reviewRequired) {
        setError('Review the saved currency, prices and tax rates. Reopen this draft from the PO list before making another change.');
        return;
      }
      setReviewed(true);
      setError('The PO is still Draft. Review the saved attachments below before resolving the uncertain result.');
    } catch (cause) { if (alive.current) setError(poSaveError(cause)); }
    finally { running.current = false; }
  };
  const resolveEntry = (key, wasSaved) => {
    if (!reviewed || running.current) return;
    const row = queueRef.current.find(item => item.key === key);
    if (!row || row.state !== 'uncertain') return;
    if (!window.confirm(wasSaved
      ? 'Confirm you inspected the saved file below and this queued copy is no longer needed. This does not delete any server document.'
      : 'Confirm you inspected the saved files and this file was NOT saved. A later Save/Submit may upload it again.')) return;
    updateQueue(rows => wasSaved ? rows.filter(item => item.key !== key) : rows.map(item => item.key === key ? { ...item, state: 'queued' } : item));
  };
  const resume = () => {
    if (!reviewed || queueRef.current.some(row => row.state === 'uncertain') || running.current) return;
    if (window.confirm('I reviewed the server state and attachments. Allow the next explicit Save/Submit action?')) {
      setPaused(null); setError('');
    }
  };
  return { queue, documents, listError, listLoading, error, phase, paused, reviewed, addFiles, editEntry, removeEntry, run,
    reload: () => reload().catch(() => {}), reviewSavedState, resolveEntry, resume,
  };
}
